// @flow
import * as React from 'react';

import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { Icon } from '@ta/styleguide.icon';
import { localize } from '@ta/common.i18n';
import { InteractionLogger } from '@ta/tracking.interactions';
import type { ExternalRepostProps } from '@ta/social.types';

import styles from './ShareCta.less';
import ShareTracker from './ShareTracker';
import { SharePopover } from './SharePopover';

/** External Props for ShareCta. */
export type ExternalShareCtaProps = {|
  /** See ShareTargetProps */
  styleAsSocialAction: boolean,
  /** A header element to show above the menu items */
  headerComponent?: React.Node,
  /** Optional callback to fire when the cta is clicked */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,
  /** Which edge to align the popup */
  alignEdge: 'right' | 'left',
  /** Optional repost props */
  repostProps?: ExternalRepostProps,
  /** Optional to position the popover above the cta. If omitted, defaults to below */
  above: boolean,
  /** Optional shift the popover in the X axis in pixels */
  shiftX: number,
  /** Optional shift the popover in the y axis in pixels */
  shiftY: number,
  /** Optional prop to hide the text */
  hideText?: boolean,
|};

/** All props for ShareCta. */
export type ShareCtaProps = {|
  /** the above */
  ...ExternalShareCtaProps,
  /** The URL to share */
  url: string,
  /** For use in tracking: What type of item are we sharing? */
  itemType: string,
  /** For use in tracking: What is the ID of the item? e.g. if it is a POI, what is the locationID */
  itemId: string,
|};

/** Props for ShareTarget. */
type ShareTargetProps = {
  /** Style it as an action in the social action bar (as opposed to a stand-alone CTA).
   *  Default is stand-alone */
  styleAsSocialAction: boolean,
  /** Action to call when the target is clicked */
  onClick: (SyntheticMouseEvent<HTMLElement>) => mixed,
  /** 'target' method of modals and popups */
  target: () => void,
  /** Optional prop to hide the text */
  hideText?: boolean,
};

/** Wraps the ShareTarget.  On click of ShareTarget, a SharePopover appears with various ways to share the post. */
const ShareCta = ({
  url,
  itemType,
  itemId,
  styleAsSocialAction,
  headerComponent,
  onClick,
  alignEdge,
  repostProps,
  above,
  shiftX,
  shiftY,
  hideText,
}: ShareCtaProps) => (
  <div>
    <SharePopover
      url={url}
      itemType={itemType}
      itemId={itemId}
      shiftX={shiftX}
      shiftY={shiftY}
      headerComponent={headerComponent}
      alignEdge={alignEdge}
      repostProps={repostProps}
      above={above}
    >
      {(target, open) => (
        <ShareTracker itemType={itemType} itemId={itemId} url={url} action="share_button_click">
          {() => (
            <InteractionLogger>
              {logInteraction => (
                <ShareTarget
                  onClick={e => {
                    if (onClick) {
                      onClick(e);
                    }
                    logInteraction(e, 'share');
                    open();
                  }}
                  target={target}
                  styleAsSocialAction={styleAsSocialAction}
                  hideText={hideText}
                />
              )}
            </InteractionLogger>
          )}
        </ShareTracker>
      )}
    </SharePopover>
  </div>
);

ShareCta.defaultProps = {
  styleAsSocialAction: false,
  headerComponent: null,
  onClick: undefined,
  above: false,
  shiftX: 6,
  shiftY: 4,
  hideText: false,
};

/** The displayed component for ShareCta.  Renders an icon and the text 'Share'.  Clicking invokes onClick callback (see ShareCta). */
const ShareTarget = ({ onClick, target, styleAsSocialAction, hideText }: ShareTargetProps) => (
  <div
    onClick={onClick}
    className={classNames(
      styleAsSocialAction ? styles.social_action : styles.cta,
      featureIsEnabled('hr_dark_green_icons') ? styles.dark_icons : '',
    )}
  >
    <span ref={target}>
      <Icon name="repost-fill" className={styleAsSocialAction ? styles.icon : styles.cta_icon} />
      {!hideText && (
        <span
          className={
            styleAsSocialAction ? classNames('ui_link', styles.text, 'socialCTA') : styles.cta_text
          }
        >
          {localize('link_share_share')} {/* Share */}
        </span>
      )}
    </span>
  </div>
);

ShareTarget.defaultProps = {
  styleAsSocialAction: false,
  hideText: false,
};

export { ShareCta, ShareTarget };
